import { useState } from "react";
import { Layers, X, ChevronDown, ChevronRight, PenLine, Wrench } from "lucide-react";
import * as api from "../../lib/api";
import { PRIMARY } from "./shared";

export default function GroupPlanModal({ groups, onClose, onAdded }) {
  const [expanded, setExpanded] = useState({});
  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState(null);

  const toggle = (i) => setExpanded(e => ({ ...e, [i]: !e[i] }));

  const handleAdd = async () => {
    setPushing(true);
    try {
      const r = await api.groupGscQueriesToRecommendations(groups);
      setPushed(r.added);
      onAdded?.(r.added);
    } catch (e) {
      console.error(e);
    } finally {
      setPushing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <div className="flex items-center gap-2">
            <Layers size={16} className="text-zinc-700" />
            <h2 className="font-bold text-zinc-900 text-sm">Content Plan — {groups.length} groups</h2>
          </div>
          <button onClick={onClose} className="p-1 text-zinc-400 hover:text-zinc-700">
            <X size={16} />
          </button>
        </div>

        {/* Groups */}
        <div className="overflow-y-auto flex-1 p-4 space-y-2">
          {groups.map((g, i) => (
            <div key={i} className="border border-zinc-100 rounded-xl overflow-hidden">
              <button
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 text-left"
                onClick={() => toggle(i)}
              >
                <span className="flex-1 font-semibold text-zinc-900 text-sm">{g.label}</span>
                <span
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
                  style={g.action === "optimize"
                    ? { background: "#FFFBEB", color: "#B45309" }
                    : { background: "#ECFDF5", color: "#065F46" }}
                >
                  {g.action === "optimize"
                    ? <><Wrench size={9} /> Optimize</>
                    : <><PenLine size={9} /> Write Article</>}
                </span>
                <span className="text-xs text-zinc-400 tabular-nums">{g.query_count} queries</span>
                {expanded[i]
                  ? <ChevronDown size={13} className="text-zinc-400 flex-shrink-0" />
                  : <ChevronRight size={13} className="text-zinc-400 flex-shrink-0" />}
              </button>
              {expanded[i] && (
                <div className="px-4 pb-3 pt-0 bg-zinc-50 border-t border-zinc-100">
                  {g.article_title && (
                    <p className="text-[11px] text-amber-700 font-medium mb-2">
                      Target page: {g.article_title}
                    </p>
                  )}
                  <ul className="space-y-0.5">
                    {g.queries.map((q, j) => (
                      <li key={j} className="text-xs text-zinc-600 truncate">{q.query}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-zinc-100">
          {pushed != null
            ? <span className="text-sm font-bold text-green-700">Added {pushed} suggestions to Recommendations</span>
            : <span className="text-xs text-zinc-400">Each group becomes one recommendation card</span>}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-zinc-600 border border-zinc-100 rounded-lg hover:bg-zinc-50"
            >
              {pushed != null ? "Close" : "Cancel"}
            </button>
            {pushed == null && (
              <button
                onClick={handleAdd}
                disabled={pushing}
                className="px-4 py-2 text-sm font-bold text-white rounded-lg disabled:opacity-40"
                style={{ backgroundColor: PRIMARY }}
              >
                {pushing ? "Adding…" : "Add to Recommendations"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
